import {createSelector} from 'reselect'

import {PICK_MODE} from '../../../common/constants/PackingOrderModal.js'
import {
  setForm,
  removeForm,
  formsSelector,
  onlyIfForm,
  useSelector,
  updateForm,
} from '../../../store.js'
import {plural} from '../../../common/components/Plural.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import PackPanel from '../PackPanel/index.js'
import {
  packPanelFormSelector,
  PACK_PANEL_BARCODE_SCANNED,
} from '../PackPanel/packPanelSelectors.js'
import useToggleTimer from '../../../common/hooks/useToggleTimer.js'
import {ordersSelector} from '../../../data/orders.js'
import {
  setPickOrderWarehouseNotice,
  showPickOrderWarehouseNoticeSelector,
} from '../../../data/me.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'

export const MODAL_FORM = 'PACKING_ORDER_MODAL'

export function showPickOrdersModal(orderNumbers = []) {
  setForm(MODAL_FORM, {
    orderNumbers,
  })
}

function updateModalForm(updates, meta) {
  updateForm(MODAL_FORM, updates, meta)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

function warehouseOrderSummarySelector(state) {
  const {orderNumbers} = modalFormSelector(state)

  const orders = ordersSelector(state, {orderNumbers})

  return Object.values(
    orders.reduce((prev, order) => {
      prev[order.warehouse.id] ||= []

      prev[order.warehouse.id].push(order)

      return prev
    }, {}),
  )
}

function PickOrdersModal({form: {orderNumbers}}) {
  const [animateSuccess, startSuccessAnimation] = useToggleTimer()
  const {scanResult, needsFocus} = useSelector(packPanelFormSelector) || {}
  const warehouseOrderSummary = useSelector(warehouseOrderSummarySelector)
  const showPickOrderWarehouseNotice = useSelector(
    showPickOrderWarehouseNoticeSelector,
  )

  return (
    <ConfirmModal
      title={plural(orderNumbers)`Pick Order${['s']}`}
      className="modal--lg modal--scan-mode meta-verify-order-modal"
      onCancel={() => closeModal()}
      cancelText="Cancel"
      badgeAnimate={animateSuccess}
      badgeContent={
        scanResult === PACK_PANEL_BARCODE_SCANNED ? 'Scan Successful' : null
      }
      helpLink="https://support.ordoro.com/how-to-use-barcode-scanning-to-reduce-packing-errors/"
      helpMetaClass="meta-verify-order-support-link"
    >
      {showPickOrderWarehouseNotice &&
        warehouseOrderSummary.length > 1 &&
        !needsFocus && (
          <div className="alert alert--warning w-75 margin-auto margin-bottom-25">
            <p className="fs-01 lh-md margin-bottom-5">
              <strong>
                You have selected orders with items assigned to different
                warehouses.
              </strong>
            </p>
            <p className="fs-01 lh-md margin-bottom-15">
              To prevent picking inaccuracies, we recommend selecting orders
              with products assigned to the same warehouse.
            </p>
            <ButtonPrimary
              isOutlined
              size="x-sm"
              onClick={() => setPickOrderWarehouseNotice(false)}
            >
              Hide this warning
            </ButtonPrimary>
          </div>
        )}
      <PackPanel
        orderNumbers={orderNumbers}
        mode={PICK_MODE}
        onScan={() => startSuccessAnimation()}
        inModal
        onOrderNumbersChange={(orderNumbers) => updateModalForm({orderNumbers})}
      />
    </ConfirmModal>
  )
}

export default onlyIfForm(PickOrdersModal, modalFormSelector)
